'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { Breadcrumbs, Button, Header, Select, SponsoredBannersProvider } from '@/components';
import { JobCard } from '@/components/sections/CareersJobListingsSection';
import LogoLoader from '@/components/ui/LogoLoader';
import SearchIcon from '@/components/icons/SearchIcon';
import HeartIcon from '@/components/icons/HeartIcon';
import GiftsIcon from '@/components/icons/GiftsIcon';
import StarFilledIcon from '@/components/icons/StarFilledIcon';
import UserIcon from '@/components/icons/UserIcon';
import type { Store } from '@/lib/types/stores';
import type { CareerJob } from '@/lib/careers-jobs';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

const PERKS = [
  {
    title: 'Real growth path',
    description: 'Coaching, certifications, and clear ladders from Budtender to Lead to Manager.',
    icon: StarFilledIcon,
  },
  {
    title: 'Team that has your back',
    description: 'Tight-knit crews, transparent communication, and managers who actually listen.',
    icon: UserIcon,
  },
  {
    title: 'Generous perks',
    description: 'Staff discount on everything, paid training, and flexible scheduling.',
    icon: GiftsIcon,
  },
  {
    title: 'Cannabis culture',
    description: 'Work alongside people who care about the plant, the customer, and the craft.',
    icon: HeartIcon,
  },
];

export default function CareersPage() {
  const [stores, setStores] = useState<Store[]>([]);
  const [jobs, setJobs] = useState<CareerJob[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data?.stores) setStores(data.stores);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/careers`, { cache: 'no-store' });
        if (!cancelled && res.ok) {
          const data = await res.json();
          if (data?.success && Array.isArray(data.jobs)) {
            setJobs(
              data.jobs.map((j: any) => ({
                id: j.slug,
                title: j.title,
                category: j.category || '',
                description: j.description || '',
                location: j.location || '',
                employmentType: j.employmentType || '',
                responsibilities: j.responsibilities || [],
                skills: j.skills || [],
                plusPoints: j.plusPoints || [],
              })),
            );
          }
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const availableLocations = useMemo(() => {
    const s = new Set<string>();
    jobs.forEach((j) => j.location && s.add(j.location));
    return ['all', ...Array.from(s).sort()];
  }, [jobs]);

  const availableTypes = useMemo(() => {
    const s = new Set<string>();
    jobs.forEach((j) => j.employmentType && s.add(j.employmentType));
    return ['all', ...Array.from(s).sort()];
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => {
      if (locationFilter !== 'all' && j.location !== locationFilter) return false;
      if (typeFilter !== 'all' && j.employmentType !== typeFilter) return false;
      if (q) {
        const hay = `${j.title} ${j.category} ${j.description} ${j.location}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, search, locationFilter, typeFilter]);

  return (
    <SponsoredBannersProvider>
      <div className="min-h-screen">
        <Header stores={stores} base={API_BASE} />

        <main className="space-y-12 px-4 pb-20 sm:space-y-16 sm:px-8">
          {/* HERO ─────────────────────────────────────────────────────────── */}
          <section className="relative mt-4 overflow-hidden sm:mt-6" aria-labelledby="careers-hero-heading">
            <div className="mx-auto w-full">
              <div className="relative w-full overflow-hidden rounded-2xl bg-brand-100 sm:rounded-2xl">
                <div className="pointer-events-none absolute inset-0 z-0">
                  <Image
                    src="/media/pages/hero-bg-image.webp"
                    alt=""
                    fill
                    priority
                    className="object-cover object-center opacity-[0.05]"
                    sizes="100vw"
                    aria-hidden
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 z-[1] bg-brand-100/10" aria-hidden />

                <div className="relative z-[2] flex flex-col gap-6 p-4 sm:gap-8 sm:p-6 lg:p-8">
                  <div className="min-w-0 max-w-2xl flex-1">
                    <Breadcrumbs items={[{ label: 'Careers' }]} tone="onDark" />

                    <h1
                      id="careers-hero-heading"
                      className="text-h2-mobile font-bold leading-tight text-white lg:text-h2-desktop"
                    >
                      Join Our Team
                    </h1>

                    <p className="mt-4 text-body-16-desktop font-normal leading-relaxed text-white/90">
                      We&apos;re hiring across our Alberta stores. If you enjoy working with customers and want
                      to grow with a busy team, we want to hear from you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* PERKS BAND ───────────────────────────────────────────────────── */}
          <section aria-labelledby="careers-perks-heading">
            <h2 id="careers-perks-heading" className="text-h3-mobile font-bold text-text-primary sm:text-h3-desktop">
              Why work at Dank?
            </h2>
            <p className="mt-2 max-w-2xl text-body-md text-text-secondary sm:text-body-16-desktop">
              We&apos;re building a calm, professional team that takes the craft seriously and treats teammates well.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:mt-8 sm:grid-cols-2 lg:grid-cols-4">
              {PERKS.map((perk) => {
                const Icon = perk.icon;
                return (
                  <article
                    key={perk.title}
                    className="rounded-2xl border border-neutral-10 bg-constant-white p-5 transition-colors duration-200 hover:border-blue-15"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-5 text-blue-60">
                      <Icon size={20} color="currentColor" />
                    </div>
                    <h3 className="mt-4 text-body-18 font-bold text-text-primary">{perk.title}</h3>
                    <p className="mt-1.5 text-body-sm leading-relaxed text-text-secondary">{perk.description}</p>
                  </article>
                );
              })}
            </div>
          </section>

          {/* OPEN ROLES ──────────────────────────────────────────────────── */}
          <section aria-labelledby="careers-open-roles-heading">
            <div>
              <h2 id="careers-open-roles-heading" className="text-h3-mobile font-bold text-text-primary sm:text-h3-desktop">
                Open roles
              </h2>
              <p className="mt-2 text-body-md text-text-secondary sm:text-body-16-desktop">
                Browse current opportunities and reach out, we&apos;ll get back to you as soon as we can.
              </p>
            </div>

            {/* Search + filters, all controls share the same 52px height for visual harmony */}
            {!loading && jobs.length > 0 && (
              <div className="mt-4 flex w-full flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative w-full flex-1 sm:max-w-md">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-40">
                    <SearchIcon size={14} color="currentColor" />
                  </span>
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search roles, keywords, locations…"
                    className="h-10 w-full rounded-lg border border-neutral-10 bg-constant-white pl-8 pr-3 text-[14px] text-text-primary placeholder:text-neutral-40 focus:border-blue-60 focus:outline-none focus:ring-2 focus:ring-blue-15"
                  />
                </div>

                <div className="flex flex-1 items-stretch gap-2 sm:flex-none">
                  {availableLocations.length > 1 && (
                    <div className="w-1/2 sm:w-48">
                      <Select
                        value={locationFilter}
                        onChange={setLocationFilter}
                        options={availableLocations.map((opt) => ({
                          value: opt,
                          label: opt === 'all' ? 'All locations' : opt,
                        }))}
                        className="w-full"
                      />
                    </div>
                  )}
                  {availableTypes.length > 1 && (
                    <div className="w-1/2 sm:w-48">
                      <Select
                        value={typeFilter}
                        onChange={setTypeFilter}
                        options={availableTypes.map((opt) => ({
                          value: opt,
                          label: opt === 'all' ? 'All types' : opt,
                        }))}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Listings */}
            {loading ? (
              <div className="mt-4 flex min-h-[280px] items-center justify-center">
                <LogoLoader size="md" label="Loading roles…" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-dashed border-neutral-10 bg-constant-white p-10 text-center">
                <p className="text-h4-mobile font-bold text-text-primary sm:text-h4-desktop">No open roles right now</p>
                <p className="mt-2 text-body-md text-text-secondary">
                  Check back soon, new opportunities open up all the time.
                </p>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-dashed border-neutral-10 bg-constant-white p-10 text-center">
                <p className="text-h4-mobile font-bold text-text-primary sm:text-h4-desktop">No roles match your filters</p>
                <p className="mt-2 text-body-md text-text-secondary">Try clearing the search or pick a different location.</p>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => {
                    setSearch('');
                    setLocationFilter('all');
                    setTypeFilter('all');
                  }}
                  className="mt-5 h-10 rounded-xl border border-blue-10 bg-constant-white px-5 text-body-sm font-semibold text-blue-80"
                >
                  Reset filters
                </Button>
              </div>
            ) : (
              <ul className="mt-4 flex w-full list-none flex-col gap-4 p-0 sm:gap-6">
                {filteredJobs.map((job) => (
                  <li key={job.id} className="w-full">
                    <JobCard job={job} />
                  </li>
                ))}
              </ul>
            )}
          </section>

        </main>
      </div>
    </SponsoredBannersProvider>
  );
}

